import { Checkbox, FormControlLabel, TextField } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getGroupInfo, getGroupTemplates } from '../../../../../../services/groups.service';
import { Select } from '../../../../../Common/Select';
import { IFormattedFunctionality } from '../../../Modals/CreateGroup';
import { AccessCheckboxList, CardTitle, Container, InputContainer } from './styles';

export interface IFunctionality {
    id: string;
    name: string;
}

interface IAcessCardProps {
    selectedPackageIsAdmin: boolean;
    functionalities: IFormattedFunctionality[];
    setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    selectedFuncionalities: string[];
    setSelectedFuncionalities: React.Dispatch<React.SetStateAction<string[]>>;
    setFunctionalities: React.Dispatch<React.SetStateAction<IFormattedFunctionality[]>>;
}

const AcessCard: React.FC<IAcessCardProps> = ({
    selectedFuncionalities,
    setSelectedFuncionalities,
    selectedPackageIsAdmin,
    functionalities,
    setFunctionalities,
    setLoading = (active: boolean) => {return;},
}) => {
    const { t } = useTranslation();

    const [nameFilter, setNameFilter] = useState<string>('');
    const [groupTemplates, setGroupTemplates] = useState<{id: string, name: string}[]>([]);
    const [selectedGroupTemplate, setSelectedGroupTemplate] = useState<{id: string, name: string}>({} as {id: string, name: string});

    const fetchGroupTemplates = useCallback(async() => {
        setLoading(true);
        
        const data = await getGroupTemplates();

        setGroupTemplates(data.groups);

        setLoading(false);
    }, []);

    useEffect(() => {
        fetchGroupTemplates();
    }, [fetchGroupTemplates]);
    
    useEffect(() => {
        if (nameFilter?.length >= 3) {
            const delayDebounceFn = setTimeout(() => {
                const filteredFunctionalityIds = functionalities.filter((functionality) => t(`softwareManagement.modules.groups.functionalities.${functionality.name}`).toLowerCase().includes(nameFilter.toLowerCase())).map((functionality) => functionality.id);
                
                const newFunctionalities = functionalities.map((functionality) => {
                    if (filteredFunctionalityIds.includes(functionality.id)) {
                        return {
                            ...functionality,
                            hidden: false,
                        }
                    }

                    return {
                        ...functionality,
                        hidden: true,
                    }
                });

                setFunctionalities(newFunctionalities);
            }, 1000);

            return () => clearTimeout(delayDebounceFn)
        } else {
            setFunctionalities((oldValue) => oldValue.map((functionality) => ({
                ...functionality,
                hidden: false,
            })));
        }
    }, [nameFilter]);

    const handleAddOrRemoveFunctionality = (functionalityId: string) => {
        if (selectedFuncionalities.includes(functionalityId)) {
            setSelectedFuncionalities(selectedFuncionalities.filter((functionality) => functionality !== functionalityId));
        } else {
            setSelectedFuncionalities([...selectedFuncionalities, functionalityId]);
        }
    };

    const handleSelectTemplate = useCallback(async (templateGroup: {id: string; name: string}) => {
        const group = await getGroupInfo(templateGroup.id);

        const templateFunctionalities = group.functionalities.map((functionality: {id: string; name: string}) => functionality.id);

        setSelectedFuncionalities(templateFunctionalities);

        setSelectedGroupTemplate(templateGroup);
    }, []);

    return (
        <Container>
            <CardTitle>Acessos</CardTitle>

            <InputContainer>
                <TextField
                    autoComplete='nope' 
                    name="name"
                    id="name"
                    label={t("forms.filterByName")}
                    defaultValue=""
                    fullWidth
                    value={nameFilter}
                    onChange={(value) => setNameFilter(value.target.value)}
                />
            </InputContainer>

            <AccessCheckboxList>
                {functionalities.filter(f => !f.hidden).map((functionality) => (
                    <FormControlLabel
                        key={functionality.id}
                        control={
                            <Checkbox
                                value={selectedFuncionalities.includes(functionality.id)}
                                checked={selectedFuncionalities.includes(functionality.id)}
                                onChange={() => handleAddOrRemoveFunctionality(functionality.id)}
                                name={functionality.id}
                                id={functionality.id}
                                color="primary"
                            />
                        }
                        label={t(`softwareManagement.modules.groups.functionalities.${functionality.name}`)}
                    />
                ))}
            </AccessCheckboxList>

            <InputContainer>
                <Select
                    id="accessTemplate"
                    options={groupTemplates}
                    value={selectedGroupTemplate}
                    setValue={handleSelectTemplate}
                    label='Copiar de um template'
                    disabled={selectedPackageIsAdmin}
                />
            </InputContainer>
        </Container>
    );
}

export default AcessCard;
